import React, { useState } from 'react';
import secureApiClient from '../utils/secureApiClient';

// Same DSM-V / ICD-10 screening questions previously hosted as a static
// form on the public website. Kept verbatim so existing clinical wording
// is unchanged; scoring itself is left to the AI analysis pipeline
// (analyzeFormSubmission) rather than duplicated here.
const QUESTION_GROUPS = [
  {
    category: 'anxiety',
    title: 'Anxiety',
    groups: [
      {
        key: 'groupA', title: 'Core Symptoms', questions: [
          { id: 'anxA1', label: 'I experience persistent and unrealistic worry about various events or situations' },
          { id: 'anxA2', label: 'I feel restless or on edge, having difficulty staying calm' },
          { id: 'anxA3', label: 'I experience fatigue even after minimal activity' },
          { id: 'anxA4', label: 'I have difficulty concentrating or my mind goes blank' },
        ]
      },
      {
        key: 'groupB', title: 'Additional Symptoms', questions: [
          { id: 'anxB1', label: 'I feel easily annoyed or short-tempered' },
          { id: 'anxB2', label: 'I experience muscle tension, aches, or stiffness' },
          { id: 'anxB3', label: 'I have difficulty falling or staying asleep, or experience restless sleep' },
          { id: 'anxB4', label: 'I experience shortness of breath or difficulty breathing' },
          { id: 'anxB5', label: 'I have an accelerated heart rate or palpitations' },
          { id: 'anxB6', label: 'I sweat excessively, even without physical exertion' },
          { id: 'anxB7', label: 'I feel dizzy or lightheaded' },
          { id: 'anxB8', label: 'I experience nausea or stomach discomfort' },
        ]
      },
    ]
  },
  {
    category: 'panic',
    title: 'Panic Attack',
    groups: [
      {
        key: 'groupC', title: 'Group C', questions: [
          { id: 'pncC1', label: 'I have had an episode of intense fear or discomfort that started abruptly, without warning' },
          { id: 'pncC2', label: 'It escalated to a peak within a few minutes and lasted several minutes' },
        ]
      },
      {
        key: 'groupD', title: 'Group D (Autonomic arousal)', questions: [
          { id: 'pncD1', label: 'I experience heart palpitations' },
          { id: 'pncD2', label: 'I sweat excessively' },
          { id: 'pncD3', label: 'I tremble or shake' },
          { id: 'pncD4', label: 'I have a dry mouth' },
        ]
      },
      {
        key: 'groupE', title: 'Group E (Other possible symptoms)', questions: [
          { id: 'pncE1', label: 'I experience chest pain' },
          { id: 'pncE2', label: 'I feel numbness or tingling in my body' },
          { id: 'pncE3', label: 'I have nausea or stomach problems' },
          { id: 'pncE4', label: 'I feel dizzy or lightheaded' },
          { id: 'pncE5', label: 'I feel detached from myself (depersonalization) or reality (derealization)' },
          { id: 'pncE6', label: 'I fear losing control' },
          { id: 'pncE7', label: 'I have an intense fear of dying' },
        ]
      },
    ]
  },
  {
    category: 'depression',
    title: 'Depression',
    groups: [
      {
        key: 'core', title: 'Core Symptoms', questions: [
          { id: 'depC1', label: 'I have persistent low mood most of the day, nearly every day, for at least two weeks' },
          { id: 'depC2', label: 'I have lost interest or pleasure in activities I usually enjoy' },
          { id: 'depC3', label: 'I have a lack of energy or unusual fatigue almost every day' },
        ]
      },
      {
        key: 'additional', title: 'Additional Symptoms', questions: [
          { id: 'depA1', label: 'I have significantly reduced ability to concentrate or focus' },
          { id: 'depA2', label: 'I have noticed a decrease in my self-esteem or self-confidence' },
          { id: 'depA3', label: 'I have frequent feelings of excessive guilt or worthlessness' },
          { id: 'depA4', label: 'I have thoughts of death or suicide' },
          { id: 'depA5', label: 'I have significant changes in sleep (insomnia or excessive sleeping)' },
          { id: 'depA6', label: 'I have significant changes in appetite (increase or decrease)' },
          { id: 'depA7', label: 'I feel restless or physically slowed down' },
        ]
      },
    ]
  },
  {
    category: 'adhd',
    title: 'ADHD (Attention-Deficit/Hyperactivity Disorder)',
    groups: [
      {
        key: 'inattention', title: 'Inattention', questions: [
          { id: 'adhdI1', label: 'I often fail to give close attention to details or make careless mistakes' },
          { id: 'adhdI2', label: 'I have difficulty sustaining attention in tasks or play activities' },
          { id: 'adhdI3', label: 'I do not seem to listen when spoken to directly' },
          { id: 'adhdI4', label: 'I do not follow through on instructions and fail to finish tasks' },
          { id: 'adhdI5', label: 'I have difficulty organizing tasks and activities' },
          { id: 'adhdI6', label: 'I avoid or dislike tasks requiring sustained mental effort' },
          { id: 'adhdI7', label: 'I frequently lose things necessary for tasks or activities' },
          { id: 'adhdI8', label: 'I am easily distracted by extraneous stimuli' },
          { id: 'adhdI9', label: 'I am often forgetful in daily activities' },
        ]
      },
      {
        key: 'hyperactivity', title: 'Hyperactivity-Impulsivity', questions: [
          { id: 'adhdH1', label: 'I fidget with or tap my hands or feet or squirm in my seat' },
          { id: 'adhdH2', label: 'I leave my seat in situations when remaining seated is expected' },
          { id: 'adhdH3', label: 'I run about or climb in inappropriate situations' },
          { id: 'adhdH4', label: 'I am unable to play or engage in leisure activities quietly' },
          { id: 'adhdH5', label: 'I am often "on the go," acting as if "driven by a motor"' },
          { id: 'adhdH6', label: 'I talk excessively' },
          { id: 'adhdH7', label: 'I blurt out an answer before a question has been completed' },
          { id: 'adhdH8', label: 'I have difficulty waiting my turn' },
          { id: 'adhdH9', label: 'I interrupt or intrude on others' },
        ]
      },
    ]
  },
  {
    category: 'adjustment',
    title: 'Adjustment Disorder',
    groups: [
      {
        key: 'symptoms', title: null, questions: [
          { id: 'adj1', label: 'My symptoms developed within one month of a clear stressor' },
          { id: 'adj2', label: 'My symptoms feel out of proportion to the severity of that stressor' },
          { id: 'adj3', label: 'I am experiencing clinically significant distress or impairment in functioning' },
          { id: 'adj4', label: 'I feel these symptoms do not meet criteria for another disorder' },
          { id: 'adj5', label: 'My symptoms have not persisted more than 6 months after the stressor ended' },
        ]
      },
    ]
  },
];

const emptyBasicInfo = {
  age: '',
  maritalStatus: '',
  children: '',
  medicalCondition: '',
  previousDiagnosis: '',
  medications: '',
  additionalInfo: '',
};

const SelfAssessmentForm = ({ onSubmitted }) => {
  const [answers, setAnswers] = useState({});
  const [basicInfo, setBasicInfo] = useState(emptyBasicInfo);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const toggle = (id) => setAnswers(prev => ({ ...prev, [id]: !prev[id] }));
  const updateBasicInfo = (field, value) => setBasicInfo(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const selectedCheckboxes = {};
    for (const cat of QUESTION_GROUPS) {
      selectedCheckboxes[cat.category] = {};
      for (const group of cat.groups) {
        selectedCheckboxes[cat.category][group.key] = group.questions
          .filter(q => answers[q.id])
          .map(q => q.label);
      }
    }

    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.submitSelfAssessment,
        { method: 'POST', body: JSON.stringify({ ...basicInfo, selectedCheckboxes }) }
      );
      setSubmitted(true);
      setAnswers({});
      setBasicInfo(emptyBasicInfo);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err.message || 'Failed to submit assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-xl p-8 text-center border" style={{ backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }}>
        <div className="text-4xl mb-3">✅</div>
        <h3 className="font-semibold mb-1" style={{ color: '#15803d' }}>Assessment submitted</h3>
        <p className="text-sm text-gray-600">Your therapist will review your responses.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-sm text-gray-500 leading-relaxed">
        Please check any symptoms that apply to you over the past two weeks. Your therapist will
        review your responses — there's no need to interpret them yourself.
      </p>

      {error && (
        <div className="px-4 py-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {/* Basic clinical info */}
      <div className="rounded-xl p-5 border border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
        <h3 className="text-sm font-semibold mb-4" style={{ color: '#434344' }}>Basic Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <label className="text-xs font-medium" style={{ color: '#7d9483' }}>
            Age
            <input type="number" value={basicInfo.age} onChange={e => updateBasicInfo('age', e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
          </label>
          <label className="text-xs font-medium" style={{ color: '#7d9483' }}>
            Marital Status
            <input type="text" value={basicInfo.maritalStatus} onChange={e => updateBasicInfo('maritalStatus', e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
          </label>
          <label className="text-xs font-medium" style={{ color: '#7d9483' }}>
            Children
            <input type="text" value={basicInfo.children} onChange={e => updateBasicInfo('children', e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
          </label>
          <label className="text-xs font-medium" style={{ color: '#7d9483' }}>
            Current Medications
            <input type="text" value={basicInfo.medications} onChange={e => updateBasicInfo('medications', e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
          </label>
          <label className="text-xs font-medium col-span-2" style={{ color: '#7d9483' }}>
            Current Medical Conditions
            <input type="text" value={basicInfo.medicalCondition} onChange={e => updateBasicInfo('medicalCondition', e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
          </label>
          <label className="text-xs font-medium col-span-2" style={{ color: '#7d9483' }}>
            Previous Psychiatric Diagnoses
            <input type="text" value={basicInfo.previousDiagnosis} onChange={e => updateBasicInfo('previousDiagnosis', e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
          </label>
          <label className="text-xs font-medium col-span-2" style={{ color: '#7d9483' }}>
            Additional Relevant Information
            <textarea rows={3} value={basicInfo.additionalInfo} onChange={e => updateBasicInfo('additionalInfo', e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none" />
          </label>
        </div>
      </div>

      {/* Symptom checklists */}
      {QUESTION_GROUPS.map(cat => (
        <div key={cat.category} className="rounded-xl p-5 border border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
          <h3 className="text-sm font-semibold mb-4" style={{ color: '#1e562d' }}>{cat.title}</h3>
          <div className="space-y-4">
            {cat.groups.map(group => (
              <div key={group.key}>
                {group.title && (
                  <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#7d9483' }}>
                    {group.title}
                  </p>
                )}
                <div className="space-y-2">
                  {group.questions.map(q => (
                    <label key={q.id} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!answers[q.id]}
                        onChange={() => toggle(q.id)}
                        className="mt-1"
                      />
                      <span>{q.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="px-5 py-2.5 rounded-lg text-sm font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: '#1e562d' }}
        >
          {submitting ? 'Submitting…' : 'Submit Assessment'}
        </button>
      </div>
    </form>
  );
};

export default SelfAssessmentForm;
